import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import Dialog from '@material-ui/core/Dialog';
import store from '../../redux/store';
import { updateFunctionGraphText } from '../../redux/actions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { DialogTitle, Typography } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Slider from '@material-ui/core/Slider';
import { CirclePicker } from 'react-color';
import functionPlot from '../../pages/LessonPage/functionPlot';
import { GA_EVENT_TREE } from '../../gaConstants';

const DEFAULT_AZIMUTH = -60;
const DEFAULT_ELEVATION = 30;

// Same material palette used by the shape and connector color menus.
const PICKER_COLORS = ['#000000', '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722', '#795548', '#607d8b'];

const FunctionGraphEditDialog = ({ lessonState }) => {
    let [dialogEditFunctionGraph, setDialogEditFunctionGraph] = useState(null);
    let [expression, setExpression] = useState('');
    let [xMin, setXMin] = useState('-10');
    let [xMax, setXMax] = useState('10');
    let [yMin, setYMin] = useState('-10');
    let [yMax, setYMax] = useState('10');
    let [azimuth, setAzimuth] = useState(DEFAULT_AZIMUTH);
    let [elevation, setElevation] = useState(DEFAULT_ELEVATION);
    let [color, setColor] = useState('#000000');
    const intl = useIntl();

    useEffect(() => {
        setDialogEditFunctionGraph(lessonState.dialogEditFunctionGraph);
        if (lessonState.dialogEditFunctionGraph?.el) {
            const el = lessonState.dialogEditFunctionGraph.el;
            setExpression(el.expression || '');
            setXMin('' + (Number.isFinite(el.xMin) ? el.xMin : -10));
            setXMax('' + (Number.isFinite(el.xMax) ? el.xMax : 10));
            setYMin('' + (Number.isFinite(el.yMin) ? el.yMin : -10));
            setYMax('' + (Number.isFinite(el.yMax) ? el.yMax : 10));
            setAzimuth(Number.isFinite(el.azimuth) ? el.azimuth : DEFAULT_AZIMUTH);
            setElevation(Number.isFinite(el.elevation) ? el.elevation : DEFAULT_ELEVATION);
            setColor(el.color || '#000000');
        }
    }, [lessonState.dialogEditFunctionGraph]);

    const expressionCheck = functionPlot.validate(expression);
    // Expressions containing y are 3D surfaces and get y-range + view controls.
    const usesY = expressionCheck.valid && expressionCheck.usesY;
    const rangeValid =
        xMin.trim() !== '' && xMax.trim() !== '' &&
        Number.isFinite(parseFloat(xMin)) && Number.isFinite(parseFloat(xMax)) &&
        parseFloat(xMin) < parseFloat(xMax);
    const yRangeValid =
        yMin.trim() !== '' && yMax.trim() !== '' &&
        Number.isFinite(parseFloat(yMin)) && Number.isFinite(parseFloat(yMax)) &&
        parseFloat(yMin) < parseFloat(yMax);
    const allValid = expressionCheck.valid && rangeValid && (!usesY || yRangeValid);

    const close = () => {
        store.dispatch(updateFunctionGraphText(null));
    };

    // Rotation and tilt are committed as soon as a slider is released, so the
    // view is saved and applied to the board even if the dialog is closed
    // without OK. Only the angles are passed — the update helper ignores
    // undefined fields, so a half-typed expression is never committed here.
    const commitView = (azimuthValue, elevationValue) => {
        if (dialogEditFunctionGraph?.el && dialogEditFunctionGraph.callback) {
            dialogEditFunctionGraph.callback(
                dialogEditFunctionGraph.el.id,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                azimuthValue,
                elevationValue,
            );
        }
    };

    // Color is committed immediately on selection, like the view sliders.
    const commitColor = (hex) => {
        setColor(hex);
        if (dialogEditFunctionGraph?.el && dialogEditFunctionGraph.callback) {
            dialogEditFunctionGraph.callback(
                dialogEditFunctionGraph.el.id,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                hex,
            );
        }
    };

    const updateFunctionGraph = () => {
        if (!allValid) return;
        if (dialogEditFunctionGraph?.el && dialogEditFunctionGraph.callback) {
            window.logAppActivity(GA_EVENT_TREE.lessonPage.toolSelection.functionGraphEdit);
            dialogEditFunctionGraph.callback(
                dialogEditFunctionGraph.el.id,
                expression.trim(),
                parseFloat(xMin),
                parseFloat(xMax),
                parseFloat(yMin),
                parseFloat(yMax),
                azimuth,
                elevation,
                color,
            );
        }
        setDialogEditFunctionGraph(null);
        store.dispatch(updateFunctionGraphText(null));
    };

    return (
        <Dialog open={!!dialogEditFunctionGraph} onClose={close}>
            <DialogTitle>
                <FormattedMessage id="generic.label.update" />
            </DialogTitle>
            <DialogContent>
                <Typography>
                    <FormattedMessage id="lessonPage.functionGraphEdit" />
                </Typography>
                {dialogEditFunctionGraph ? (
                    <>
                        <TextField
                            value={expression}
                            onChange={(event) => {
                                setExpression(event.target.value);
                            }}
                            label={intl.formatMessage({ id: 'lessonPage.functionGraph.expression' })}
                            error={!expressionCheck.valid}
                            helperText={
                                expressionCheck.valid
                                    ? ' '
                                    : intl.formatMessage({ id: 'lessonPage.functionGraph.invalidExpression' })
                            }
                            required
                            fullWidth
                            variant="outlined"
                            autoFocus={true}
                            margin="normal"
                        />
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <TextField
                                value={xMin}
                                onChange={(event) => {
                                    setXMin(event.target.value);
                                }}
                                label={intl.formatMessage({ id: 'lessonPage.functionGraph.xMin' })}
                                type="number"
                                error={!rangeValid}
                                helperText={' '}
                                required
                                variant="outlined"
                                margin="normal"
                            />
                            <TextField
                                value={xMax}
                                onChange={(event) => {
                                    setXMax(event.target.value);
                                }}
                                label={intl.formatMessage({ id: 'lessonPage.functionGraph.xMax' })}
                                type="number"
                                error={!rangeValid}
                                helperText={
                                    rangeValid
                                        ? ' '
                                        : intl.formatMessage({ id: 'lessonPage.functionGraph.invalidRange' })
                                }
                                required
                                variant="outlined"
                                margin="normal"
                            />
                        </div>
                        {usesY ? (
                            <>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <TextField
                                        value={yMin}
                                        onChange={(event) => {
                                            setYMin(event.target.value);
                                        }}
                                        label={intl.formatMessage({ id: 'lessonPage.functionGraph.yMin' })}
                                        type="number"
                                        error={!yRangeValid}
                                        helperText={' '}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                    <TextField
                                        value={yMax}
                                        onChange={(event) => {
                                            setYMax(event.target.value);
                                        }}
                                        label={intl.formatMessage({ id: 'lessonPage.functionGraph.yMax' })}
                                        type="number"
                                        error={!yRangeValid}
                                        helperText={
                                            yRangeValid
                                                ? ' '
                                                : intl.formatMessage({ id: 'lessonPage.functionGraph.invalidYRange' })
                                        }
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </div>
                                <Typography variant="caption">
                                    <FormattedMessage id="lessonPage.functionGraph.azimuth" />
                                </Typography>
                                <Slider
                                    value={azimuth}
                                    onChange={(event, value) => {
                                        setAzimuth(value);
                                    }}
                                    onChangeCommitted={(event, value) => {
                                        setAzimuth(value);
                                        commitView(value, elevation);
                                    }}
                                    min={-180}
                                    max={180}
                                    step={5}
                                    valueLabelDisplay="auto"
                                />
                                <Typography variant="caption">
                                    <FormattedMessage id="lessonPage.functionGraph.elevation" />
                                </Typography>
                                <Slider
                                    value={elevation}
                                    onChange={(event, value) => {
                                        setElevation(value);
                                    }}
                                    onChangeCommitted={(event, value) => {
                                        setElevation(value);
                                        commitView(azimuth, value);
                                    }}
                                    min={0}
                                    max={90}
                                    step={5}
                                    valueLabelDisplay="auto"
                                />
                            </>
                        ) : null}
                        <Typography variant="caption">
                            <FormattedMessage id="lessonPage.functionGraph.color" />
                        </Typography>
                        <div style={{ margin: '0.5rem 0' }}>
                            <CirclePicker
                                disableAlpha
                                width="100%"
                                circleSize={22}
                                circleSpacing={8}
                                color={color}
                                colors={PICKER_COLORS}
                                onChangeComplete={(c) => {
                                    commitColor(c.hex);
                                }}
                            />
                        </div>
                        <div
                            style={{
                                width: '100%',
                                height: '12rem',
                                color: color,
                                opacity: allValid ? 1 : 0.35,
                            }}
                            dangerouslySetInnerHTML={{
                                __html: functionPlot.buildFunctionGraphSvg({
                                    expression: expression.trim(),
                                    xMin: parseFloat(xMin),
                                    xMax: parseFloat(xMax),
                                    yMin: parseFloat(yMin),
                                    yMax: parseFloat(yMax),
                                    azimuth,
                                    elevation,
                                    color: color,
                                    weight: dialogEditFunctionGraph?.el?.weight,
                                }),
                            }}
                        />
                    </>
                ) : null}
            </DialogContent>
            <DialogActions style={{ marginBottom: '0.5rem', marginRight: '1rem' }}>
                <Button onClick={updateFunctionGraph} onTouchStart={updateFunctionGraph} color="primary" disabled={!allValid}>
                    <FormattedMessage id="ok" />
                </Button>
                <Button onClick={close} onTouchStart={close} color="primary">
                    <FormattedMessage id="close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const mapStateToProps = (state) => {
    const { lessonState } = state;
    return { lessonState };
};

export default connect(mapStateToProps)(FunctionGraphEditDialog);
